import React, { useState, Fragment } from 'react';
import { Filter, Download, Search, ChevronRight, ChevronDown, FileText } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
type ClaimItem = {
  id: number;
  po: string;
  claimType: string;
  amount: string;
  status: 'recovered' | 'pending' | 'lost';
  outcome: string;
};
type Batch = {
  id: string;
  retailer: string;
  check: string;
  dateReceived: string;
  items: ClaimItem[];
};
export function Ledger() {
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const toggleBatch = (id: string) => {
    const newExpanded = new Set(expandedBatches);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedBatches(newExpanded);
  };
  const batches: Batch[] = [{
    id: 'b1',
    retailer: 'Dollar General',
    check: '#DG-9982',
    dateReceived: 'Feb 18, 2026',
    items: [{
      id: 1,
      po: 'PO-44821',
      claimType: 'Shortage',
      amount: '$500.00',
      status: 'recovered',
      outcome: 'Won'
    }, {
      id: 2,
      po: 'PO-44822',
      claimType: 'Pricing',
      amount: '$320.00',
      status: 'pending',
      outcome: 'In Progress'
    }, {
      id: 3,
      po: 'PO-44823',
      claimType: 'Shortage',
      amount: '$180.00',
      status: 'lost',
      outcome: 'Lost (Valid)'
    }]
  }, {
    id: 'b2',
    retailer: 'Amazon VC',
    check: '#AMZ-8821',
    dateReceived: 'Feb 18, 2026',
    items: [{
      id: 4,
      po: 'PO-88201',
      claimType: 'Shortage',
      amount: '$1,200.00',
      status: 'recovered',
      outcome: 'Won'
    }, {
      id: 5,
      po: 'PO-88202',
      claimType: 'Co-op',
      amount: '$2,500.00',
      status: 'recovered',
      outcome: 'Won'
    }, {
      id: 6,
      po: 'PO-88203',
      claimType: 'Pricing',
      amount: '$450.00',
      status: 'pending',
      outcome: 'In Progress'
    }]
  }, {
    id: 'b3',
    retailer: 'Walmart',
    check: '#WM-2219',
    dateReceived: 'Feb 17, 2026',
    items: [{
      id: 7,
      po: 'PO-22190',
      claimType: 'Shortage',
      amount: '$875.00',
      status: 'recovered',
      outcome: 'Won'
    }, {
      id: 8,
      po: 'PO-22191',
      claimType: 'Shortage',
      amount: '$340.00',
      status: 'lost',
      outcome: 'Lost (Valid)'
    }]
  }, {
    id: 'b4',
    retailer: 'Dollar General',
    check: '#DG-1102',
    dateReceived: 'Feb 2, 2026',
    items: [{
      id: 9,
      po: 'PO-11021',
      claimType: 'Co-op',
      amount: '$1,100.00',
      status: 'recovered',
      outcome: 'Won'
    }, {
      id: 10,
      po: 'PO-11022',
      claimType: 'Shortage',
      amount: '$220.00',
      status: 'pending',
      outcome: 'In Progress'
    }]
  }];
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'recovered':
        return 'success';
      case 'lost':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'neutral';
    }
  };
  return <div className="space-y-6" data-id="element-460">
      <div className="flex items-center justify-between" data-id="element-461">
        <div data-id="element-462">
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight" data-id="element-463">
            Ledger
          </h2>
          <p className="text-slate-500 mt-1" data-id="element-464">
            All disputed claims and recovery status grouped by batch.
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-465">
          <button className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center space-x-2 transition-colors" data-id="element-466">
            <Download className="w-4 h-4" data-id="element-467" />
            <span data-id="element-468">Export CSV</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-469">
        {/* Filters */}
        <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-4 bg-white" data-id="element-470">
          <div className="relative flex-1 max-w-md" data-id="element-471">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-472" />
            <input type="text" placeholder="Search by PO#, Check, or Retailer..." className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" data-id="element-473" />
          </div>
          <div className="flex items-center space-x-2" data-id="element-474">
            <button className="px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center space-x-2 transition-colors" data-id="element-475">
              <Filter className="w-4 h-4" data-id="element-476" />
              <span data-id="element-477">Filter</span>
            </button>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-478">
          <table className="w-full text-sm text-left" data-id="element-479">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-480">
              <tr data-id="element-481">
                <th className="px-6 py-3 w-12" data-id="element-482"></th>
                <th className="px-6 py-3" data-id="element-483">Retailer / Check #</th>
                <th className="px-6 py-3" data-id="element-484">Date Received</th>
                <th className="px-6 py-3" data-id="element-485">Total Claims</th>
                <th className="px-6 py-3" data-id="element-486"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-487">
              {batches.map(batch => {
              const isExpanded = expandedBatches.has(batch.id);
              return <Fragment key={batch.id} data-id="element-488">
                    {/* Batch Row */}
                    <tr className={`cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50'}`} onClick={() => toggleBatch(batch.id)} data-id="element-489">
                      <td className="px-6 py-4 text-slate-400" data-id="element-490">
                        {isExpanded ? <ChevronDown className="w-5 h-5" data-id="element-491" /> : <ChevronRight className="w-5 h-5" data-id="element-492" />}
                      </td>
                      <td className="px-6 py-4" data-id="element-493">
                        <div className="font-semibold text-slate-900 text-base" data-id="element-494">
                          {batch.retailer}
                        </div>
                        <div className="text-xs text-slate-500 font-mono mt-0.5" data-id="element-495">
                          {batch.check}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600 font-medium" data-id="element-496">
                        {batch.dateReceived}
                      </td>
                      <td className="px-6 py-4" data-id="element-497">
                        <div className="flex items-center space-x-2" data-id="element-498">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800" data-id="element-499">
                            {batch.items.length} items
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-500">
                        {/* Placeholder for potential row actions */}
                      </td>
                    </tr>

                    {/* Expanded Child Rows */}
                    {isExpanded && <tr className="bg-slate-50/50" data-id="element-501">
                        <td colSpan={5} className="px-0 py-0 border-t border-slate-100" data-id="element-502">
                          <div className="py-2" data-id="element-503">
                            <table className="w-full" data-id="element-504">
                              <thead className="text-xs text-slate-400 uppercase tracking-wider" data-id="element-505">
                                <tr data-id="element-506">
                                  <th className="px-6 py-2 pl-20 text-left font-medium" data-id="element-507">
                                    PO #
                                  </th>
                                  <th className="px-6 py-2 text-left font-medium" data-id="element-508">
                                    Claim Type
                                  </th>
                                  <th className="px-6 py-2 text-left font-medium" data-id="element-509">
                                    Amount
                                  </th>
                                  <th className="px-6 py-2 text-left font-medium" data-id="element-510">
                                    Status
                                  </th>
                                  <th className="px-6 py-2 text-left font-medium" data-id="element-511">
                                    Outcome
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100/50" data-id="element-512">
                                {batch.items.map(item => <tr key={item.id} className="hover:bg-white transition-colors" data-id="element-513">
                                    <td className="px-6 py-3 pl-20" data-id="element-514">
                                      <div className="flex items-center space-x-2" data-id="element-515">
                                        <FileText className="w-3.5 h-3.5 text-slate-400" data-id="element-516" />
                                        <span className="font-medium text-slate-700" data-id="element-517">
                                          {item.po}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-6 py-3 text-slate-600" data-id="element-518">
                                      {item.claimType}
                                    </td>
                                    <td className="px-6 py-3 font-medium text-slate-900" data-id="element-519">
                                      {item.amount}
                                    </td>
                                    <td className="px-6 py-3" data-id="element-520">
                                      <StatusBadge status={getStatusVariant(item.status)} data-id="element-521">
                                        {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                      </StatusBadge>
                                    </td>
                                    <td className="px-6 py-3 text-slate-600" data-id="element-522">
                                      {item.outcome}
                                    </td>
                                  </tr>)}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>}
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-slate-100 flex items-center justify-between text-sm text-slate-500 bg-white" data-id="element-523">
          <span data-id="element-524">Showing {batches.length} batches</span>
          <div className="flex space-x-2" data-id="element-525">
            <button className="px-3 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 transition-colors" disabled data-id="element-526">
              Previous
            </button>
            <button className="px-3 py-1 border border-slate-200 rounded hover:bg-slate-50 transition-colors" data-id="element-527">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>;
}